import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useStatsigClient } from "@statsig/react-bindings";
import { isMobile } from "react-device-detect";

import { ReactComponent as HistoryLogo } from "../../../../Assets/Nav/nav-history.svg";
import {
  ActiveOverlayPopUpState,
  UniqueIdentifierState,
} from "MVVM/State/iframe";
import { IClip, ESupportedGames } from "MVVM/@types";
import { TUserRole } from "../../@types";
import theme from "theme/themeThree";
import { Button } from "MVVM/Components/Button";
import { MatchHistoryWidgetViewModel } from "./ViewModel";
import { URLS } from "MVVM/Utilities/Maps";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { CloseButton } from "../../styledComponents";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { useEffect, useState } from "react";
import "./index.css";
import {
  PlaylistIdState,
  FinalClipInPlaylist,
} from "MVVM/State/embeddablePlaylist";
import { MatchHistorySearch } from "MVVM/Components/MatchHistorySearch";
import { MatchHistorySearchBarIsOpenState } from "MVVM/State/matchHistory";
import { isPartner, Partner } from "MVVM/Views/EmbeddablePlaylist/Utilities";

const VALID_GAME_IDS = ["CS2", "LOL"];

export const MatchHistoryWidget = ({
  clip,
  userRole = "creator",
}: {
  clip: IClip;
  userRole?: TUserRole;
}) => {
  const uniqueIdentifier = useRecoilValue(UniqueIdentifierState);
  const playerGameIdentifier =
    getMatchHistoryGameEnum(clip.game) === ESupportedGames.LOL
      ? clip.playerGameIdentifier
      : uniqueIdentifier || clip.playerGameIdentifier;

  const [selectedPlayerGameId, setSelectedPlayerGameId] =
    useState(playerGameIdentifier);
  const [widgetTitle, setWidgetTitle] = useState("");

  const { moments, loading } = MatchHistoryWidgetViewModel({
    playerGameId: selectedPlayerGameId,
  });
  const platform = new URLSearchParams(window.location.search).get("platform");
  const playlistId = useRecoilValue(PlaylistIdState);
  const { track } = useAnalyticsContext();
  const [hasTrackedWidgetDisplayed, setHasTrackedWidgetDisplayed] =
    useState(false);
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );
  const isFinalClipInPLaylist = useRecoilValue(FinalClipInPlaylist);
  const { getExperiment } = useStatsigClient();

  const isSearchBarOpen = useRecoilValue(MatchHistorySearchBarIsOpenState);

  const { t } = useTranslation("translation", {
    keyPrefix: "embeddablePlayer.matchHistoryWidget",
  });

  const clipButtonText = userRole === "viewer" ? "Watch" : "Clip";

  useEffect(() => {
    if (uniqueIdentifier && userRole !== "viewer") {
      setWidgetTitle("More of your moments");
    }

    if (selectedPlayerGameId !== playerGameIdentifier) {
      const username = moments.length
        ? `from ${moments[0]?.player?.displayName}`
        : "";
      setWidgetTitle(`Recent Top Plays ${username}`);
    } else {
      setWidgetTitle(`Recent Top Plays from ${clip.user.username}`);
    }
  }, [
    setWidgetTitle,
    clip.user.username,
    moments,
    uniqueIdentifier,
    userRole,
    selectedPlayerGameId,
    playerGameIdentifier,
  ]);

  useEffect(() => {
    setHasTrackedWidgetDisplayed(false);
  }, [clip.internalId]);

  useEffect(() => {
    if (
      activeOverlayPopUp === null &&
      selectedPlayerGameId !== playerGameIdentifier
    ) {
      setSelectedPlayerGameId(playerGameIdentifier);
    }
  }, [activeOverlayPopUp, playerGameIdentifier, selectedPlayerGameId]);

  if (
    !VALID_GAME_IDS.includes(clip.game) ||
    (activeOverlayPopUp !== "history" &&
      activeOverlayPopUp !== "nextUpPlaylist") ||
    isFinalClipInPLaylist
  ) {
    return null;
  }

  if (moments.length > 0 && !hasTrackedWidgetDisplayed) {
    setHasTrackedWidgetDisplayed(true);
    track("Component Viewed", {
      locationIdentifier: "match_history_widget",
      location: "embeddable_player_overlay",
    });
  }

  if (!loading && moments.length === 0)
    return (
      <MatchHistoryContainer className="match-history-container">
        <EmptyState />
      </MatchHistoryContainer>
    );

  const showMatchHistorySearch = isPartner(platform, Partner.HLTV);
  const showProfileCTA = getExperiment("hltv_pro_player_profile_ctas").get(
    "show_profile_cta",
    false,
  );

  return (
    <MatchHistoryContainer className="match-history-container">
      <Header className="header">
        <Flex style={{ gap: 8, alignItems: "center" }}>
          <StyledHistoryLogo className="styled-history-logo" />
          <StyledTitle variant="text.titleXSmall" className="styled-title">
            {widgetTitle}
          </StyledTitle>
        </Flex>
        <CloseButton
          className="close-button"
          onClick={() => setActiveOverlayPopUp(null)}
        />
      </Header>

      {!isSearchBarOpen && (
        <MomentsColumn className="moments-column">
          {loading &&
            [1, 1, 1].map((_, index) => (
              <LoadingSkeleton
                key={index}
                containerClassName="loading-skeleton-moment-row"
                width="100%"
                height="36px"
                style={{
                  borderRadius: "4px",
                  marginBottom: "8px",
                }}
              />
            ))}
          {!loading && moments.length === 0 && <EmptyState />}
          {!loading &&
            moments.length > 0 &&
            moments?.map((moment) => (
              <MomentRow
                className="moment-row"
                onClick={() => {
                  track("Button - Click", {
                    locationIdentifier: "match_history_widget_button",
                    location: "embeddable_player_overlay",
                  });
                  const ajs_aid = window.rudderanalytics.getAnonymousId();
                  const utm_source =
                    clip.developer?.slug || platform || "unknown";

                  if (
                    showMatchHistorySearch &&
                    showProfileCTA &&
                    moment.player.gameId
                  ) {
                    const domain = window.location.origin;
                    const profileUrl = `${domain}/u/${moment.player.gameId}?utm_source=${platform}&ajs_aid=${ajs_aid}`;
                    return window.open(profileUrl, "_blank");
                  }

                  if (platform === "TRACKERNETWORKLLC") {
                    return window.open(
                      `${window.location.origin}/clip/${clip.shareId}?ajs_aid=${ajs_aid}&utm_source=${utm_source}&context=playlist&playlistId=${playlistId}`,
                      "_blank",
                    );
                  }

                  const renderLinkToAppStore = getExperiment(
                    "playlist_to_app_store",
                  ).get("appone_redirect", false);
                  if (renderLinkToAppStore && isMobile) {
                    window.open(
                      `https://allstar.gg/apponelink?ajs_aid=${ajs_aid}&utm_source=${utm_source}`,
                      "_blank",
                    );
                  } else {
                    window.open(
                      `${URLS.MATCH_HISTORY}?matchId=${
                        moment.match.id
                      }&game=${getMatchHistoryGameEnum(
                        moment.game,
                      )}&playerSearchIdentifier=${
                        moment.player.gameId
                      }&ajs_aid=${window.rudderanalytics.getAnonymousId()}&momentId=${
                        moment.id
                      }&utm_source=${
                        clip.developer?.slug ||
                        new URLSearchParams(window.location.search).get(
                          "platform",
                        ) ||
                        "unknown"
                      }`,
                      "_blank",
                    );
                  }
                }}
              >
                <MomentTitleAndTime className="moment-title-time">
                  <MomentTime className="moment-time">
                    {moment.timelineLabel}
                  </MomentTime>
                  <MomentTitle className="moment-title">
                    {moment.title}
                  </MomentTitle>
                </MomentTitleAndTime>

                <MomentElimsRow className="moment-elims-row">
                  {moment.player.avatar && (
                    <PlayerIcon
                      className="player-icon"
                      src={moment.player.avatar}
                    />
                  )}

                  {moment.icons[0] && (
                    <WeaponIcon
                      className="weapon-icon"
                      src={moment.icons[0]}
                      color={theme.colors.ice}
                    />
                  )}

                  <PlayersRow>
                    {moment.victimData.map((victim) => (
                      <PlayerIcon className="player-icon" src={victim.avatar} />
                    ))}
                  </PlayersRow>
                </MomentElimsRow>
                <ClipButton
                  className="clip-button"
                  classification="main"
                  flex={true}
                >
                  <ClipButtonText
                    className="clip-button-text"
                    style={{ color: theme.colors.ice }}
                  >
                    {clipButtonText}
                  </ClipButtonText>
                </ClipButton>
              </MomentRow>
            ))}
        </MomentsColumn>
      )}
      {showMatchHistorySearch && (
        <StyledSearchContainer $isSearchBarOpen={isSearchBarOpen}>
          <StyledSearchSubText className="search-sub-text">
            See if we have matches ready for you!
          </StyledSearchSubText>
          <MatchHistorySearch
            locationIdentifier="matchHistoryWidget"
            placeholder="Search by Steam ID or Username..."
            onPlayerSelect={(gameId: string) => setSelectedPlayerGameId(gameId)}
          />
        </StyledSearchContainer>
      )}
    </MatchHistoryContainer>
  );
};

const EmptyState = () => {
  const setActiveOverlayPopUp = useSetRecoilState(ActiveOverlayPopUpState);
  const { track } = useAnalyticsContext();

  useEffect(() => {
    track("Component Viewed", {
      locationIdentifier: "match_history_widget_no_moments",
      location: "embeddable_player_overlay",
    });
  }, [track, setActiveOverlayPopUp]);

  return (
    <EmptyStateContainer>
      <EmptyStateCloseButton
        className="close-button"
        onClick={() => setActiveOverlayPopUp(null)}
      />
      <EmptyStateHeader className="empty-state-header">
        No Moments Found
      </EmptyStateHeader>
      <EmptyStateSubText className="empty-state-sub-text">
        This player doesn't have any additional moments to watch right now.
      </EmptyStateSubText>
    </EmptyStateContainer>
  );
};

const EmptyStateCloseButton = styled(CloseButton)`
  align-self: flex-end;
  margin-left: auto;
  margin-right: -16px;
`;

const EmptyStateContainer = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  padding-top: 0px;
  text-align: center;
`;

const EmptyStateHeader = styled(Text)`
  color: ${theme.colors.ice};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  line-height: 14px;
`;

const EmptyStateSubText = styled(Text)`
  color: #f7fcfdcc;
  font-family: Barlow;
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
`;

const StyledTitle = styled(Text)`
  font-size: 11px;
  line-height: 11px;
`;

const StyledHistoryLogo = styled(HistoryLogo)`
  fill: ${theme.colors.envy};
  height: 11px;
  width: 11px;
`;

const ClipButtonText = styled(Text)`
  font-family: Barlow;
  font-size: 8px;
  font-weight: 500;
  leading-trim: Cap height;
  letter-spacing: 0%;
  line-height: 10px;
  margin-top: -2px;
`;

const ClipButton = styled(Button)`
  align-items: center;
  border-radius: 99px;
  gap: 4px;
  height: 18px;
  justify-content: center;
  padding-left: 6px;
  padding-right: 6px;
  padding-top: 2px;
  text-align: center;
`;

const PlayersRow = styled(Flex)`
  align-items: center;
  flex-direction: row;
`;
const MomentElimsRow = styled(Flex)`
  align-items: center;
  flex-direction: row;
  gap: 4px;
  height: 100%;
  justify-content: center;
  margin-right: 10px;
  margin-top: 2px;
`;

const PlayerIcon = styled(Image)`
  border-color: #000000;
  border-radius: 50%;
  border-width: 1px;
  flex-shrink: 0;
  height: 10px;
  margin-left: 0px;
  width: 10px;

  &:not(:first-child) {
    margin-left: -7px;
  }
`;

const WeaponIcon = styled(Image)`
  height: 6px;
  width: auto;
  filter: brightness(0) saturate(100%) invert(96%) sepia(14%) saturate(174%)
    hue-rotate(164deg) brightness(102%) contrast(98%);
`;

const Header = styled(Flex)`
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  padding-left: 8px;
  padding-right: 6px;
  padding-top: 6px;
`;

const MomentTime = styled(Text)`
  color: #f7fcfdcc;
  font-family: Barlow;
  font-size: 8px;
  font-weight: 500;
  height: 10px;
  letter-spacing: 0%;
  line-height: 10px;
  width: 23px;
`;
const MomentTitle = styled(Text)`
  font-family: Barlow;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`;
const MomentsColumn = styled(Flex)`
  flex-direction: column;
`;

const MomentTitleAndTime = styled(Flex)`
  align-items: center;
  align-self: center;
  flex-direction: row;
  gap: 8px;
  height: 100%;
  width: 134px;
`;

const MomentRow = styled(Flex)`
  border-radius: 4px;
  cursor: pointer;
  gap: 8px;
  justify-content: space-between;
  padding: 8px;
  width: 100%;

  &:hover {
    background-color: #ffffff1a;
  }
`;
const MatchHistoryContainer = styled(Flex)`
  backdrop-filter: blur(4px);
  background-color: #000000bf;
  border-radius: 12px;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  pointer-events: auto;
  width: 246px;
`;

const StyledSearchSubText = styled(Text)`
  color: ${({ theme }) => theme.colors.error};
  font-size: 12px;
  line-height: 14px;
  font-weight: 500;
`;

const StyledSearchContainer = styled(Flex)<{ $isSearchBarOpen: boolean }>`
  align-items: center;
  flex-direction: column;
  gap: 2px;

  ${({ $isSearchBarOpen }) =>
    $isSearchBarOpen &&
    `
    min-height: 160px;
  `}
`;
